/**
 * Mounts the session management system for authenticated routes:
 *   - activity detector → heartbeat → warning modal
 *   - cross-tab signout via BroadcastChannel
 *
 * Render this inside RequireAuth (or AppLayout) so it only runs when logged in.
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { subscribe, type SessionMessage } from "./broadcastChannel";
import { startActivityDetector } from "./activityDetector";
import { startHeartbeat, recordActivity } from "./heartbeat";
import { WarningModal } from "./WarningModal";
import { apiClient } from "@shared/api/client";

// Warn when ≤ 2 minutes remain on idle timeout.
const WARN_THRESHOLD_SECONDS = 120;

interface SessionContextValue {
  secondsUntilIdle: number;
  secondsUntilAbsolute: number;
}

const SessionContext = createContext<SessionContextValue>({
  secondsUntilIdle: 1800,
  secondsUntilAbsolute: 86400,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useSession() {
  return useContext(SessionContext);
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [secondsUntilIdle, setSecondsUntilIdle] = useState(1800);
  const [secondsUntilAbsolute, setSecondsUntilAbsolute] = useState(86400);
  const [showWarning, setShowWarning] = useState(false);
  const warningCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [warningCountdown, setWarningCountdown] = useState(WARN_THRESHOLD_SECONDS);

  const doSignOut = useCallback(
    (reason: string) => {
      stopWarningCountdown();
      navigate(`/login?reason=${reason}`);
    },
    [navigate]
  );

  function stopWarningCountdown() {
    if (warningCountdownRef.current) {
      clearInterval(warningCountdownRef.current);
      warningCountdownRef.current = null;
    }
    setShowWarning(false);
  }

  function startWarningCountdown(seconds: number) {
    setWarningCountdown(seconds);
    setShowWarning(true);
    warningCountdownRef.current = setInterval(() => {
      setWarningCountdown((prev) => {
        if (prev <= 1) {
          stopWarningCountdown();
          doSignOut("inactivity");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const handleMessage = useCallback(
    (msg: SessionMessage) => {
      if (msg.type === "signout") {
        doSignOut(msg.reason);
      } else if (msg.type === "heartbeat-ack") {
        setSecondsUntilIdle(msg.secondsUntilIdle);
        setSecondsUntilAbsolute(msg.secondsUntilAbsolute);

        if (msg.secondsUntilAbsolute <= 0) {
          doSignOut("absolute");
          return;
        }

        const shouldWarn = msg.secondsUntilIdle <= WARN_THRESHOLD_SECONDS;
        if (shouldWarn && !warningCountdownRef.current) {
          startWarningCountdown(msg.secondsUntilIdle);
        } else if (!shouldWarn && warningCountdownRef.current) {
          stopWarningCountdown();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doSignOut]
  );

  useEffect(() => {
    const unsubscribe = subscribe(handleMessage);
    return unsubscribe;
  }, [handleMessage]);

  useEffect(() => {
    const stopActivity = startActivityDetector(() => {
      recordActivity();
    });
    const stopHB = startHeartbeat();
    return () => {
      stopActivity();
      stopHB();
    };
  }, []);

  async function handleStaySignedIn() {
    stopWarningCountdown();
    recordActivity();
    try {
      await apiClient.post("/api/account/heartbeat/");
    } catch {
      // heartbeat module will handle 401
    }
  }

  return (
    <SessionContext.Provider value={{ secondsUntilIdle, secondsUntilAbsolute }}>
      {children}
      {showWarning && (
        <WarningModal
          secondsRemaining={warningCountdown}
          onStaySignedIn={handleStaySignedIn}
          onSignOut={() => doSignOut("user_requested")}
        />
      )}
    </SessionContext.Provider>
  );
}
